/*
  Power Schedule widget (c2 namespace, Bootstrap 5) — drag-to-paint weekly
  scheduler for the power_schedule custom field. Attaches to a hidden
  <input> that stores CloudBolt's envelope JSON.

  See `power_schedule_codec.js` in this directory for the canonical
  codec (encode/decode/presets); it loads first via the Gulp
  `js/c2/*.js` concat (alphabetical) and exposes itself at
  `c2.powerScheduleCodec`. This file uses that single source and no
  longer carries a duplicated copy of encode/decode.

  The template's inline <script> calls c2.powerSchedule.attach(...) —
  either lazily on `shown.bs.modal` (form-embedded modal) or
  synchronously (edit dialog / order form) — and detach() on
  `hidden.bs.modal` to release document-level pointer handlers.
*/

(function (c2) {
  "use strict";

  var codec = c2.powerScheduleCodec;
  if (!codec) {
    throw new Error(
      "power_schedule_widget.js: c2.powerScheduleCodec not loaded — " +
        "power_schedule_codec.js should be concat'd before this file."
    );
  }

  var SLOTS_PER_DAY = codec.SLOTS_PER_DAY;
  var TOTAL_SLOTS = codec.TOTAL_SLOTS;
  var encode = codec.encode;
  var decode = codec.decode;
  var presetClear = codec.presetClear;
  var presetWeekdays9to5 = codec.presetWeekdays9to5;
  var presetEveryDay9to5 = codec.presetEveryDay9to5;

  var DEFAULT_DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

  // Fallback English labels. Callers thread translated values through
  // `attach({ labels: ... })` and `summarize(env, { labels: ... })` so
  // Django's i18n stays authoritative; the strings here are only shown
  // if the caller passes nothing at all.
  var DEFAULT_LABELS = {
    paint: "Paint",
    erase: "Erase",
    weekdays95: "Weekdays 9–5",
    everyday95: "Every day 9–5",
    clearAll: "Clear all",
    dragHint: "drag to select · rectangle = band across days",
    selectedBlocks: "Selected blocks",
    nothingSelected: "Nothing selected yet.",
    emptyState: "No power schedule set",
    hrsPerWeek: "hrs/week",
    block: "block",
    blocks: "blocks",
    total: "Total:",
    endOfWeek: "end of week",
  };

  function mergeLabels(overrides) {
    var out = {};
    for (var k in DEFAULT_LABELS) {
      out[k] = (overrides && overrides[k]) || DEFAULT_LABELS[k];
    }
    return out;
  }

  function escapeAttr(s) {
    // Minimal escape for interpolating a translated label into an HTML
    // attribute or text node without opening an injection vector when a
    // translator sneaks angle brackets or quotes into a message.
    return String(s == null ? "" : s)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  }

  /* ============ label helpers (UI-only, not part of the codec) */

  function pad(n) {
    return n < 10 ? "0" + n : "" + n;
  }
  function timeLabel(slot) {
    var mins = slot * 15,
      h = Math.floor(mins / 60),
      m = mins % 60;
    var ap = h < 12 || h === 24 ? "AM" : "PM";
    var hr = h % 12;
    if (hr === 0) hr = 12;
    return hr + ":" + pad(m) + " " + ap;
  }
  function shortHour(slot) {
    var h = slot / 4,
      ap = h < 12 ? "a" : "p",
      hr = h % 12;
    if (hr === 0) hr = 12;
    return hr + ap;
  }
  function idxLabel(idx, dayLabels, labels) {
    var L = labels || DEFAULT_LABELS;
    if (idx >= TOTAL_SLOTS) return L.endOfWeek;
    var d = Math.floor(idx / SLOTS_PER_DAY),
      s = idx % SLOTS_PER_DAY;
    return (dayLabels || DEFAULT_DAY_LABELS)[d] + " " + timeLabel(s);
  }
  function runs(slots) {
    var out = [],
      i = 0;
    while (i < TOTAL_SLOTS) {
      if (slots[i]) {
        var a = i;
        while (i < TOTAL_SLOTS && slots[i]) i++;
        out.push([a, i - 1]);
      } else i++;
    }
    return out;
  }

  /* ============ widget instance */

  function Widget(mount, input, opts) {
    this.mount = mount;
    this.input = input;
    // When true (used on the edit-dialog surface), every paint/preset also
    // writes the encoded envelope back to the bound input so the enclosing
    // form's submit picks it up. When false (used on the catalog order form
    // where the widget lives inside a per-field modal), commit is deferred
    // to the modal's Save button so Cancel can leave the input untouched.
    this.autoCommit = !!(opts && opts.autoCommit);
    this.dayLabels = (opts && opts.days) || DEFAULT_DAY_LABELS;
    this.labels = mergeLabels(opts && opts.labels);
    this.slots = new Uint8Array(TOTAL_SLOTS);
    this.tz = null;
    this.cells = new Array(TOTAL_SLOTS);
    this.mode = "paint";
    this.drag = { active: false, start: 0, cur: 0, prev: null };
    this._boundPointerMove = this._onPointerMove.bind(this);
    this._boundPointerUp = this._onPointerUp.bind(this);
    this.build();
    this.setValue(this.input.value);
  }

  Widget.prototype.build = function () {
    var self = this;
    var L = this.labels;
    var root = document.createElement("div");
    root.className = "power-schedule-widget";
    root.innerHTML =
      '<div class="ps-top">' +
      '<div class="seg" data-role="mode">' +
      '<button type="button" data-mode="paint" class="on">' + escapeAttr(L.paint) + "</button>" +
      '<button type="button" data-mode="erase">' + escapeAttr(L.erase) + "</button>" +
      "</div>" +
      '<button type="button" class="pill" data-preset="wd95">' + escapeAttr(L.weekdays95) + "</button>" +
      '<button type="button" class="pill" data-preset="all95">' + escapeAttr(L.everyday95) + "</button>" +
      '<button type="button" class="pill danger" data-preset="clear">' + escapeAttr(L.clearAll) + "</button>" +
      '<span class="spacer"></span>' +
      '<span class="hint">' + escapeAttr(L.dragHint) + "</span>" +
      "</div>" +
      '<div class="gridwrap"><div class="grid" data-role="grid"></div></div>' +
      '<div class="out">' +
      '<div class="panel summary"><h3>' + escapeAttr(L.selectedBlocks) + '</h3><div data-role="summary"></div></div>' +
      "</div>";
    this.mount.appendChild(root);
    this.root = root;

    var grid = root.querySelector('[data-role="grid"]');
    this.grid = grid;

    var corner = document.createElement("div");
    corner.className = "corner";
    corner.style.gridColumn = "1";
    corner.style.gridRow = "1";
    grid.appendChild(corner);
    for (var s = 0; s < SLOTS_PER_DAY; s += 8) {
      var hl = document.createElement("div");
      hl.className = "hlabel";
      hl.textContent = shortHour(s);
      hl.style.gridColumn = s + 2 + " / span 8";
      hl.style.gridRow = "1";
      grid.appendChild(hl);
    }
    for (var d = 0; d < 7; d++) {
      var dl = document.createElement("div");
      dl.className = "dlabel";
      dl.textContent = this.dayLabels[d];
      dl.style.gridColumn = "1";
      dl.style.gridRow = d + 2;
      grid.appendChild(dl);
      for (var s2 = 0; s2 < SLOTS_PER_DAY; s2++) {
        var idx = d * SLOTS_PER_DAY + s2;
        var c = document.createElement("div");
        c.className = this.baseClass(idx);
        c.dataset.idx = idx;
        c.style.gridColumn = s2 + 2;
        c.style.gridRow = d + 2;
        grid.appendChild(c);
        this.cells[idx] = c;
      }
    }

    root.querySelectorAll('[data-role="mode"] button').forEach(function (b) {
      b.addEventListener("click", function () {
        self.setMode(b.dataset.mode);
      });
    });
    root.querySelectorAll("[data-preset]").forEach(function (b) {
      b.addEventListener("click", function () {
        self.preset(b.dataset.preset);
      });
    });

    grid.addEventListener("pointerdown", function (e) {
      var cell = e.target.closest && e.target.closest("[data-idx]");
      if (!cell) return;
      e.preventDefault();
      self.drag.active = true;
      self.drag.start = +cell.dataset.idx;
      self.drag.cur = self.drag.start;
      self.drag.prev = null;
      self.updatePreview();
    });
    document.addEventListener("pointermove", this._boundPointerMove);
    document.addEventListener("pointerup", this._boundPointerUp);
  };

  Widget.prototype._onPointerMove = function (e) {
    if (!this.drag.active) return;
    var el = document.elementFromPoint(e.clientX, e.clientY);
    var cell = el && el.closest && el.closest("[data-idx]");
    if (cell) {
      this.drag.cur = +cell.dataset.idx;
      this.updatePreview();
    }
  };
  Widget.prototype._onPointerUp = function () {
    if (this.drag.active) this.commitDrag();
  };

  Widget.prototype.setMode = function (m) {
    this.mode = m;
    this.root.querySelectorAll('[data-role="mode"] button').forEach(function (b) {
      b.classList.toggle("on", b.dataset.mode === m);
      b.classList.toggle("erase", b.dataset.mode === "erase");
    });
  };

  Widget.prototype.rangeCells = function () {
    var a = Math.min(this.drag.start, this.drag.cur);
    var b = Math.max(this.drag.start, this.drag.cur);
    var arr = [];
    for (var i = a; i <= b; i++) arr.push(i);
    return arr;
  };

  function lineCls(s) {
    // "cell-h6" (not "h6") because Bootstrap 5's typography claims `.h6`
    // as a heading-style class and would apply heading margins here.
    return s % 24 === 0 ? " cell-h6" : s % 4 === 0 ? " hour" : "";
  }
  Widget.prototype.baseClass = function (idx) {
    var s = idx % SLOTS_PER_DAY;
    return "cell" + lineCls(s) + (this.slots[idx] ? " on" : "");
  };
  Widget.prototype.paintBase = function (idx) {
    this.cells[idx].className = this.baseClass(idx);
  };

  Widget.prototype.updatePreview = function () {
    var self = this;
    (this.drag.prev || []).forEach(function (idx) {
      self.paintBase(idx);
    });
    var cells = this.rangeCells();
    var pv = this.mode === "paint" ? " pv-on" : " pv-off";
    cells.forEach(function (idx) {
      self.cells[idx].className = "cell" + lineCls(idx % SLOTS_PER_DAY) + pv;
    });
    this.drag.prev = cells;
  };

  Widget.prototype.commitDrag = function () {
    var self = this;
    var v = this.mode === "paint" ? 1 : 0;
    this.rangeCells().forEach(function (idx) {
      self.slots[idx] = v;
      self.paintBase(idx);
    });
    this.drag.active = false;
    this.drag.prev = null;
    this.renderSummary();
    if (this.autoCommit) this.commit();
  };

  Widget.prototype.preset = function (p) {
    if (p === "clear") this.slots = presetClear();
    else if (p === "wd95") this.slots = presetWeekdays9to5();
    else if (p === "all95") this.slots = presetEveryDay9to5();
    for (var i = 0; i < TOTAL_SLOTS; i++) this.paintBase(i);
    this.renderSummary();
    if (this.autoCommit) this.commit();
  };

  Widget.prototype.renderSummary = function () {
    var L = this.labels;
    var r = runs(this.slots);
    var host = this.root.querySelector('[data-role="summary"]');
    if (!r.length) {
      host.innerHTML = '<span class="hint">' + escapeAttr(L.nothingSelected) + "</span>";
      return;
    }
    var total = 0;
    for (var i = 0; i < TOTAL_SLOTS; i++) total += this.slots[i];
    total *= 0.25;
    var self = this;
    host.innerHTML =
      '<ul class="runs">' +
      r
        .map(function (run) {
          return (
            "<li>" +
            idxLabel(run[0], self.dayLabels, L) +
            " → " +
            idxLabel(run[1] + 1, self.dayLabels, L) +
            "</li>"
          );
        })
        .join("") +
      "</ul>" +
      '<div class="tot">' +
      escapeAttr(L.total) +
      " " +
      total.toFixed(2) +
      " " +
      escapeAttr(L.hrsPerWeek) +
      " · " +
      r.length +
      " " +
      escapeAttr(r.length > 1 ? L.blocks : L.block) +
      "</div>";
  };

  Widget.prototype.setValue = function (jsonStr) {
    var out = decode(jsonStr);
    this.slots = out.slots;
    this.tz = out.tz;
    for (var i = 0; i < TOTAL_SLOTS; i++) this.paintBase(i);
    this.renderSummary();
  };

  // Commit the current paint state into the bound hidden <input>. Called
  // by the modal's Save button (see the widget template's inline script).
  Widget.prototype.commit = function () {
    var value = encode(this.slots, this.tz);
    this.input.value = value;
    this.input.dispatchEvent(new Event("input", { bubbles: true }));
    this.input.dispatchEvent(new Event("change", { bubbles: true }));
    return value;
  };

  Widget.prototype.destroy = function () {
    document.removeEventListener("pointermove", this._boundPointerMove);
    document.removeEventListener("pointerup", this._boundPointerUp);
    if (this.root && this.root.parentNode)
      this.root.parentNode.removeChild(this.root);
    this.cells = null;
    this.slots = null;
  };

  /* ============ public API */

  // Render a compact mini-grid preview from an envelope JSON string.
  // opts.includeRuns=true also emits the "Mon 9:00 AM → Mon 5:00 PM"
  // block list — used by the read-only view dialog where there's room
  // for it and where "when exactly" is the whole question.
  // opts.dayLabels: 7-element array of translated day names.
  // opts.labels: overrides for the built-in English strings (see
  //   DEFAULT_LABELS at the top of this file).
  // Empty / missing / malformed input yields the italic empty-state.
  function summarize(jsonStr, opts) {
    opts = opts || {};
    var dayLabels = opts.dayLabels || DEFAULT_DAY_LABELS;
    var L = mergeLabels(opts.labels);
    var emptyHtml =
      '<em class="power-schedule-summary__empty">' +
      escapeAttr(L.emptyState) +
      "</em>";
    if (!jsonStr) {
      return emptyHtml;
    }
    var decoded = decode(jsonStr);
    var r = runs(decoded.slots);
    if (!r.length) {
      return emptyHtml;
    }
    var total = 0;
    for (var i = 0; i < TOTAL_SLOTS; i++) total += decoded.slots[i];
    total *= 0.25;

    var rows = "";
    for (var d = 0; d < 7; d++) {
      var cells = "";
      for (var s = 0; s < SLOTS_PER_DAY; s++) {
        var on = decoded.slots[d * SLOTS_PER_DAY + s] ? " on" : "";
        cells += '<span class="power-schedule-mini__cell' + on + '"></span>';
      }
      rows +=
        '<div class="power-schedule-mini__row">' +
        '<span class="power-schedule-mini__day">' +
        escapeAttr(dayLabels[d]) +
        "</span>" +
        '<span class="power-schedule-mini__cells">' +
        cells +
        "</span>" +
        "</div>";
    }

    var totalLine =
      total.toFixed(2) +
      " " +
      escapeAttr(L.hrsPerWeek) +
      " · " +
      r.length +
      " " +
      escapeAttr(r.length > 1 ? L.blocks : L.block);

    var out =
      '<div class="power-schedule-mini">' + rows + "</div>";

    if (opts.includeRuns) {
      // "Selected blocks" panel — same visual shell the drag-paint
      // editor's inline summary uses (h3 header + runs list + total),
      // rebuilt here with BEM classes so the styles apply outside the
      // `.power-schedule-widget` scope. See `.power-schedule-summary__panel`
      // in _power_schedule.scss.
      out +=
        '<div class="power-schedule-summary__panel">' +
        '<h3 class="power-schedule-summary__panel-title">' +
        escapeAttr(L.selectedBlocks) +
        "</h3>" +
        '<ul class="power-schedule-summary__runs">' +
        r
          .map(function (run) {
            return (
              "<li>" +
              idxLabel(run[0], dayLabels, L) +
              " → " +
              idxLabel(run[1] + 1, dayLabels, L) +
              "</li>"
            );
          })
          .join("") +
        "</ul>" +
        '<div class="power-schedule-summary__panel-total">' +
        escapeAttr(L.total) +
        " " +
        totalLine +
        "</div>" +
        "</div>";
    } else {
      out +=
        '<div class="power-schedule-summary__total">' + totalLine + "</div>";
    }
    return out;
  }

  c2.powerSchedule = {
    attach: function (config) {
      var mount =
        typeof config.mount === "string"
          ? document.querySelector(config.mount)
          : config.mount;
      var input =
        typeof config.input === "string"
          ? document.querySelector(config.input)
          : config.input;
      if (!mount) throw new Error("c2.powerSchedule.attach: mount not found");
      if (!input) throw new Error("c2.powerSchedule.attach: input not found");
      return new Widget(mount, input, {
        autoCommit: !!config.autoCommit,
        days: config.days,
        labels: config.labels,
      });
    },
    detach: function (handle) {
      if (handle && typeof handle.destroy === "function") handle.destroy();
    },
    summarize: summarize,
  };
})((window.c2 = window.c2 || {}));
